import { BloggerQuickBlock } from "@/components/BloggerFields";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  ExpertServiceFields,
  expertServiceDraftFrom,
  expertServiceUpdate,
  type ExpertServiceDraft,
} from "@/components/ExpertServiceFields";
import { FieldError, highlightField } from "@/components/ProfileEditDrawer";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import catCelebrateAsset from "@/assets/cat_celebrate_512.png.asset.json";
import { SuggestedPeople } from "@/components/SuggestedPeople";
import {
  COFFEE_STATUS_OPTIONS,
  INTEREST_OPTIONS,
  MIN_INTERESTS,
  type Profile,
} from "@/lib/people-constants";
import { isTelegramLike, normalizeTelegram } from "@/lib/coffee-contact";
import { districtsFor } from "@/lib/districts";
import { logFunnelEvent } from "@/lib/funnel-log";
import { CitySelect } from "@/components/CitySelect";
import { postWizardKey } from "@/components/NextStep";
import { getStoredSignupSource } from "@/lib/signup-source";
import { consumeRedirectIntent, getRedirectIntent, isInviteIntent, wizardDestination } from "@/lib/redirect-intent";
import { safeReturnPath } from "@/lib/post-auth";
import { WIZARD_COPY, INTENT_CARDS, INTENT_CHIPS } from "@/lib/first-steps-copy";
import { sanitizeIntents, type PlatformIntent } from "@/lib/platform-intents";
import { detectPlatform, isStandalone } from "@/lib/device";
import { enablePush } from "@/lib/push-client";
import { InstallGuide } from "@/components/InstallGuide";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { onboardingStateKey } from "@/lib/onboarding-state";
import { factsFromProfile } from "@/lib/onboarding-facts";
import {
  OPT_OUT_STATUS,
  hasCity,
  visitorKindForCity,
  resumeIndex,
  selectSteps,
  visibleSteps,
  arenaSteps,
  wizardSteps,
  meetSteps,
  type StepDef,
  type StepId,
} from "@/lib/onboarding-steps";
import {
  WHATSAPP_ERROR,
  formatWhatsapp,
  normalizeWhatsapp,
  whatsappFromLinks,
  withInstagram,
  normalizeInstagram,
  instagramFromLinks,
  INSTAGRAM_ERROR,
  withWhatsapp,
} from "@/lib/whatsapp";
import type { PurposeState } from "@/lib/profile-purpose";
import { ChipPicker, ProfilePhotoField, PurposePicker } from "@/components/ProfileEditFields";
import { Textarea } from "@/components/ui/textarea";
import { LOOKING_FOR_MAX, SEEKING_MIN, normalizeLookingFor } from "@/lib/looking-for";
import { ARENA_COPY } from "@/lib/arena-copy";
import { useAvatarUpload } from "@/lib/avatar-upload";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MEET_OPTIONS: { value: string; label: string; note?: string }[] = [
  { value: "открыт_к_знакомствам", label: "Да, открыт(а) к знакомствам" },
  { value: "только_деловые", label: "Только по делу" },
  {
    value: OPT_OUT_STATUS,
    label: "Пока только изучаю",
    note: "Тебя не будут показывать в подборках, а я пришлю афишу встреч по твоим интересам",
  },
];
const MEET_VALUES = COFFEE_STATUS_OPTIONS.map((o) => o.value as string);
import type { Database } from "@/integrations/supabase/types";

type Row = Database["public"]["Tables"]["profiles"]["Row"];
type Update = Database["public"]["Tables"]["profiles"]["Update"];

export const Route = createFileRoute("/_authenticated/profile_/setup")({
  head: () => ({
    meta: [
      { title: "Заполни профиль за 2 минуты — TurkeyDrive" },
      {
        name: "description",
        content: "Несколько простых шагов, чтобы мы подобрали тебе людей в TurkeyDrive.",
      },
      { property: "og:title", content: "Заполни профиль за 2 минуты — TurkeyDrive" },
      {
        property: "og:description",
        content: "Несколько простых шагов, чтобы мы подобрали тебе людей в TurkeyDrive.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ProfileSetup,
});

function nameIsValid(n: string) {
  const v = n.trim();
  return v.length >= 2 && !/@[^\s@]+\.[^\s@]+/.test(v);
}

function stepsLeftLabel(n: number) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return `Остался ${n} короткий шаг`;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return `Осталось ${n} коротких шага`;
  return `Осталось ${n} коротких шагов`;
}

function ProfileSetup() {
  const { user } = useAuth();
  const uid = user?.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const shellRef = useRef<HTMLDivElement>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile-setup", uid],
    enabled: !!uid,
    queryFn: async () => {
      if (!uid) throw new Error("Пользователь не найден");
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", uid)
        .single();
      if (error) throw error;
      return data as Row;
    },
  });

  const [mode, setMode] = useState<{
    strict: boolean;
    restart: boolean;
    arena: boolean;
    meet: boolean;
    returnTo: string;
  } | null>(null);

  const { data: savedIntents } = useQuery({
    queryKey: ["platform-intents", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_platform_intents")
        .select("intents")
        .eq("user_id", uid!)
        .maybeSingle();
      if (error) return [] as PlatformIntent[];
      return sanitizeIntents(data?.intents);
    },
  });
  const [intents, setIntents] = useState<PlatformIntent[]>([]);
  const [pushStatus, setPushStatus] = useState<"idle" | "busy" | "on">("idle");
  const [iosNoInstall, setIosNoInstall] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  useEffect(() => {
    setIosNoInstall(detectPlatform() === "ios" && !isStandalone());
  }, []);

  const [allSteps, setAllSteps] = useState<StepDef[] | null>(null);
  const [index, setIndex] = useState(0);
  const [expertForm, setExpertForm] = useState(false);
  const [becameExpert, setBecameExpert] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [waError, setWaError] = useState(false);

  const [fullName, setFullName] = useState("");
  const [askName, setAskName] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoNudgeOpen, setPhotoNudgeOpen] = useState(false);
  const selfieInputRef = useRef<HTMLInputElement>(null);
  const avatarUpload = useAvatarUpload(user?.id);
  const [telegram, setTelegram] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [instagram, setInstagram] = useState("");
  const [meetChoice, setMeetChoice] = useState("");
  const [interests, setInterests] = useState<string[]>([]);
  const [profession, setProfession] = useState("");
  const [seeking, setSeeking] = useState("");
  const [about, setAbout] = useState("");
  const [purpose, setPurpose] = useState<PurposeState>({ tags: [], statuses: [] });
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [tourist, setTourist] = useState(false);
  const [expertDraft, setExpertDraft] = useState<ExpertServiceDraft>(() =>
    expertServiceDraftFrom({}),
  );

  // Режим читаем из адреса — только в браузере.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const restart = sp.get("restart") === "1";
    const arena = sp.get("mode") === "arena";
    const meet = !arena && sp.get("mode") === "meet";
    setMode({
      restart: arena || meet ? false : restart,
      strict: false,
      arena,
      meet,
      returnTo: safeReturnPath(sp.get("return")),
    });
  }, []);

  // Районы считаются по городу, выбранному прямо в мастере.
  const districtList = useMemo(() => districtsFor(city), [city]);

  // Один раз строим полный список шагов; contact/district скрываются производно по ответам.
  useEffect(() => {
    if (!profile || !mode || allSteps || savedIntents === undefined) return;
    const hasDistrictList = districtsFor(profile.city).length > 0;
    const facts = { ...factsFromProfile(profile, hasDistrictList), intents: savedIntents };
    // ARENA и «Знакомство» — только недостающие шаги; обычный мастер — намерения, город, интересы.
    const all = mode.arena
      ? arenaSteps(facts)
      : mode.meet
        ? meetSteps(facts)
        : wizardSteps(facts, mode.restart);
    setIntents(savedIntents);
    setFullName(profile.full_name ?? "");
    setAskName(!nameIsValid(profile.full_name ?? ""));
    setPhotoUrl(profile.photo_url ?? "");
    setTelegram(profile.telegram ?? "");
    setWhatsapp(whatsappFromLinks(profile.social_links));
    setInstagram(instagramFromLinks(profile.social_links));
    setMeetChoice((profile.coffee_status ?? []).find((s) => MEET_VALUES.includes(s)) ?? "");
    setInterests(profile.interests ?? []);
    setProfession(profile.profession ?? "");
    setAbout(profile.about ?? "");
    setSeeking(profile.looking_for ?? "");
    setPurpose({ tags: profile.looking_for_tags ?? [], statuses: profile.coffee_status ?? [] });
    setDistrict(profile.district ?? "");
    setCity(hasCity(profile.city) ? (profile.city ?? "").trim() : "");
    setTourist(profile.visitor_kind === "tourist");
    setExpertDraft(expertServiceDraftFrom(profile));
    setAllSteps(all);
    setIndex(0);
  }, [profile, mode, allSteps, savedIntents]);

  const arena = !!mode?.arena;
  const meetMode = !!mode?.meet;
  // «Пока только изучаю» есть только в мини-мастере «Знакомство».
  const optOut = meetMode && meetChoice === OPT_OUT_STATUS;
  const steps = useMemo(
    () =>
      allSteps
        ? arena
          ? allSteps
          : visibleSteps(allSteps, { optOut, hasDistrictList: districtList.length > 0, tourist })
        : null,
    [allSteps, arena, optOut, districtList.length, tourist],
  );
  // Мастер всегда можно закрыть: «Первые шаги» на главной предложат вернуться.
  const strictMode = false;
  const finished = !!steps && index >= steps.length;
  const current = steps && !finished ? steps[index] : null;

  // Журнал воронки (только запись, на поведение не влияет).
  useEffect(() => {
    if (!allSteps || !mode) return;
    logFunnelEvent("wizard_started", null, {
      mode: arena ? "arena" : meetMode ? "meet" : strictMode ? "strict" : "optional",
      restart: mode.restart,
      utm_source: getStoredSignupSource()?.source ?? null,
      utm_medium: getStoredSignupSource()?.medium ?? null,
      utm_campaign: getStoredSignupSource()?.campaign ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSteps, mode]);
  useEffect(() => {
    if (current?.id) logFunnelEvent("wizard_step_viewed", current.id);
  }, [current?.id]);
  useEffect(() => {
    if (finished) logFunnelEvent("wizard_completed");
  }, [finished]);

  // Финал показан — отмечаем завершение (кроме тестового перезапуска).
  const completion = useRef<Promise<void> | null>(null);
  useEffect(() => {
    if (!finished || !uid || !profile || completion.current) return;
    if (mode?.restart) {
      completion.current = Promise.resolve();
      return;
    }
    const at = new Date().toISOString();
    if (mode?.meet) {
      // Мини-мастер «Знакомство» снимает замок раздела — обновляем его кэш.
      completion.current = qc.invalidateQueries({ queryKey: onboardingStateKey(uid) });
      return;
    }
    if (!mode?.arena && profile.onboarding_completed_at) {
      completion.current = Promise.resolve();
      return;
    }
    if (mode?.arena) {
      // Мини-мастер ARENA достаточен для замка онбординга; прочим ничего не отмечаем.
      if (!profile.onboarding_gate || profile.onboarding_completed_at) {
        completion.current = Promise.resolve();
        return;
      }
    }
    completion.current = (async () => {
      await supabase.from("profiles").update({ onboarding_completed_at: at }).eq("user_id", uid);
      // Финал нового мастера сам предлагает уведомления; окно «Последний шаг» — только после ARENA.
      if (mode?.arena) {
        try {
          localStorage.setItem(postWizardKey(uid), "1");
        } catch {
          /* ignore */
        }
      }
      // Сразу обновляем кэш замка, чтобы он не вернул в мастер.
      qc.invalidateQueries({ queryKey: onboardingStateKey(uid) });
      qc.invalidateQueries({ queryKey: ["profile", uid] });
    })();
  }, [finished, uid, profile, mode?.restart, mode?.arena, mode?.meet, qc]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const viewport = window.visualViewport;
    const shell = shellRef.current;
    const updateHeight = () => {
      shell?.style.setProperty("--setup-height", `${viewport?.height ?? window.innerHeight}px`);
      shell?.style.setProperty("--setup-top", `${viewport?.offsetTop ?? 0}px`);
    };
    const keepFocusedFieldVisible = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.matches("input, textarea, [role='combobox']"))
        return;
      window.setTimeout(() => target.scrollIntoView({ block: "center", behavior: "smooth" }), 180);
    };
    updateHeight();
    viewport?.addEventListener("resize", updateHeight);
    viewport?.addEventListener("scroll", updateHeight);
    shell?.addEventListener("focusin", keepFocusedFieldVisible);
    return () => {
      viewport?.removeEventListener("resize", updateHeight);
      viewport?.removeEventListener("scroll", updateHeight);
      shell?.removeEventListener("focusin", keepFocusedFieldVisible);
    };
  }, []);

  async function save(patch: Update): Promise<boolean> {
    if (!uid) return false;
    setSaving(true);
    const { error } = await supabase.from("profiles").update(patch).eq("user_id", uid);
    setSaving(false);
    if (error) {
      toast.error("Не получилось сохранить — проверь связь и попробуй ещё раз");
      return false;
    }
    qc.invalidateQueries({ queryKey: ["profile", uid] });
    qc.invalidateQueries({ queryKey: ["profile-setup", uid] });
    qc.invalidateQueries({ queryKey: ["my-contact"] });
    return true;
  }

  /** Сохраняет данные шага вместе с onboarding_step и идёт дальше. */
  async function advance(patch: Update = {}) {
    if (!current) return;
    const step = Math.max(profile?.onboarding_step ?? 0, current.order);
    // ARENA не двигает прогресс обычного мастера.
    const ok = await save(arena ? patch : { ...patch, onboarding_step: step });
    if (!ok) return;
    setExpertForm(false);
    setIndex((i) => i + 1);
  }

  async function saveExpert() {
    try {
      const patch = expertServiceUpdate(expertDraft) as Update;
      await advance(patch);
      setBecameExpert(true);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["resident-roles"] }),
        qc.invalidateQueries({ queryKey: ["people"] }),
      ]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Не получилось сохранить услуги";
      toast.error(message);
      if (error instanceof FieldError) highlightField(error.field);
    }
  }

  async function handleNext() {
    if (!current) return;
    const id: StepId = current.id;
    if (id === "intents") {
      if (!uid) return;
      setSaving(true);
      const { error } = await supabase
        .from("user_platform_intents")
        .upsert({ user_id: uid, intents: sanitizeIntents(intents) }, { onConflict: "user_id" });
      setSaving(false);
      if (error) {
        toast.error("Не получилось сохранить — проверь связь и попробуй ещё раз");
        return;
      }
      qc.invalidateQueries({ queryKey: ["platform-intents", uid] });
      return advance();
    }
    if (id === "interests") {
      const patch: Update = { interests };
      if (askName) patch.full_name = fullName.trim();
      return advance(patch);
    }
    if (id === "city") {
      const next = city.trim();
      const patch: Update = {
        city: hasCity(next) ? next : tourist ? "Не указан" : next,
        visitor_kind: visitorKindForCity(tourist, profile?.visitor_kind),
      };
      // Сменился город — прежний район к нему не относится.
      if (next !== (profile?.city ?? "").trim() && profile?.district) {
        patch.district = null;
        patch.neighborhood = null;
        setDistrict("");
      }
      return advance(patch);
    }
    if (id === "about" && arena) {
      return advance({ profession: profession.trim() || null, about: about.trim() || null });
    }
    if (id === "purpose") return advance({ looking_for_tags: purpose.tags });
    if (id === "seeking") return advance({ looking_for: normalizeLookingFor(seeking) });
    if (id === "about") {
      return advance({
        profession: profession.trim() || null,
        looking_for_tags: purpose.tags,
      });
    }
    if (id === "meet") {
      const saved = (profile?.coffee_status ?? []).find((s) => MEET_VALUES.includes(s)) ?? "";
      // Несколько прежних статусов не затираем, если выбор не изменился.
      return advance(meetChoice && meetChoice !== saved ? { coffee_status: [meetChoice] } : {});
    }
    if (id === "contact") {
      const v = telegram.trim();
      if (v && !isTelegramLike(v)) {
        toast.error("Похоже, это не Telegram. Впиши @username");
        return;
      }
      const w = whatsapp.trim();
      const digits = w ? normalizeWhatsapp(w) : null;
      if (w && !digits) {
        setWaError(true);
        toast.error(WHATSAPP_ERROR);
        return;
      }
      const patch: Update = { telegram: v ? normalizeTelegram(v) : null };
      const cur = (profile?.social_links ?? []).filter(Boolean);
      let next = withWhatsapp(cur, digits);
      if (arena) {
        const ig = instagram.trim();
        const nick = ig ? normalizeInstagram(ig) : null;
        if (ig && !nick) {
          toast.error(INSTAGRAM_ERROR);
          return;
        }
        next = withInstagram(next, nick);
      }
      if (next.join("\n") !== cur.join("\n")) patch.social_links = next;
      return advance(patch);
    }
    if (id === "photo") return advance({ photo_url: photoUrl || null });
    return advance();
  }

  async function chooseDistrict(name: string | null) {
    const prev = profile?.district ?? null;
    setDistrict(name ?? "");
    const patch: Update = { district: name };
    // Микрорайон сбрасываем, только если район сменился.
    if (name !== prev) patch.neighborhood = null;
    await advance(patch);
  }

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  function closeWizard() {
    logFunnelEvent("wizard_closed", current?.id ?? null);
    if (meetMode) {
      if (window.history.length > 1) window.history.back();
      else navigate({ to: "/home" });
      return;
    }
    navigate({ to: "/home" });
  }

  async function goToApp(fallback: "/people" | "/events" | "/events/new" | "/arena/people" = "/people") {
    await completion.current?.catch(() => undefined);
    // Только свежая пригласительная ссылка; остальное (/profile, /home…) стирается и игнорируется.
    const target = wizardDestination(consumeRedirectIntent(), fallback);
    logFunnelEvent("post_wizard_landing", null, { path: target.split("?")[0] });
    if (target !== fallback) window.location.assign(target);
    else navigate({ to: fallback });
  }

  /** Финал по главной цели (сообщество, карточка эксперта). */
  async function goToFinal(path: string) {
    await completion.current?.catch(() => undefined);
    consumeRedirectIntent();
    logFunnelEvent("post_wizard_landing", null, { path: path.split("?")[0] });
    window.location.assign(path);
  }

  // ARENA: без финала с людьми — сразу в раздел участников.
  const leaving = useRef(false);
  useEffect(() => {
    if (!finished || !arena || leaving.current || !completion.current) return;
    leaving.current = true;
    void goToApp("/arena/people");
  });

  // Мини-мастер «Знакомство»: возвращаем туда, откуда открыли (раздел или приглашение на кофе).
  useEffect(() => {
    if (!finished || !meetMode || leaving.current || !completion.current || !mode) return;
    leaving.current = true;
    if (optOut) logFunnelEvent("wizard_opt_out");
    const target = mode.returnTo;
    void completion.current.catch(() => undefined).then(() => window.location.assign(target));
  });

  async function turnOnPush() {
    if (!uid) return;
    setPushStatus("busy");
    const res = await enablePush(uid);
    if (res === "granted") {
      setPushStatus("on");
      toast.success(WIZARD_COPY.pushOn);
    } else {
      setPushStatus("idle");
      toast.error(WIZARD_COPY.pushFailed);
    }
  }

  // Прежняя соцссылка (не WhatsApp) тоже засчитывается как контакт.
  const otherLinks = (profile?.social_links ?? []).some(
    (s) => !!s?.trim() && !/wa\.me|whatsapp/i.test(s),
  );
  const nextDisabled =
    saving ||
    (current?.id === "intents" && intents.length === 0) ||
    (current?.id === "interests" &&
      (interests.length < MIN_INTERESTS || (askName && !nameIsValid(fullName)))) ||
    (current?.id === "city" && !hasCity(city) && !tourist) ||
    (current?.id === "about" && !profession.trim()) ||
    (arena && current?.id === "about" && !about.trim()) ||
    (current?.id === "purpose" && purpose.tags.length === 0) ||
    (current?.id === "seeking" && seeking.trim().length < SEEKING_MIN) ||
    (current?.id === "meet" && !meetChoice) ||
    // Проверка формата — по нажатию «Дальше», чтобы показать понятную ошибку.
    (current?.id === "contact" && !telegram.trim() && !whatsapp.trim() && !otherLinks);

  const toggle = (arr: string[], v: string) =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
  const total = steps?.length ?? 0;
  const waDigits = whatsapp.trim() ? normalizeWhatsapp(whatsapp) : null;

  return (
    <div
      ref={shellRef}
      className={`fixed inset-x-0 top-[var(--setup-top,0px)] ${dialogOpen ? "z-40" : "z-[70]"} flex h-[var(--setup-height,100dvh)] max-h-[100dvh] flex-col overflow-hidden overflow-x-hidden bg-white`}
    >
      {finished && (meetMode || arena) ? (
        <main className="mx-auto flex w-full max-w-md flex-1 items-center justify-center px-5">
          <p className="text-center text-sm text-td-muted">
            {arena ? "Открываю «Свои на ARENA»…" : "Возвращаю тебя обратно…"}
          </p>
        </main>
      ) : finished ? (
        <>
          <main className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col items-center justify-center overflow-y-auto px-5 pb-4 pt-[calc(env(safe-area-inset-top)+16px)] text-center">
            <MrDriveAvatar variant="people" size={96} imageUrl={catCelebrateAsset.url} />
            <h1 className="mt-3 font-display text-2xl font-bold text-td-dark">
              {WIZARD_COPY.finalTitle(fullName.trim().split(/\s+/)[0] ?? "")}
            </h1>
            <p className="mt-2 text-base text-td-dark">
              {WIZARD_COPY.finalText(hasCity(city) ? city : null)}
            </p>
          </main>
          <footer className="shrink-0 border-t border-td-line bg-white px-5 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3">
            <div className="mx-auto flex max-w-md flex-col gap-2">
              {(() => {
                const invite = isInviteIntent(getRedirectIntent());
                const main = intents.includes("attend")
                  ? "attend"
                  : (["organize", "community", "expert"] as const).find((m) => intents.includes(m)) ?? "attend";
                const cta =
                  invite || main === "attend"
                    ? { label: WIZARD_COPY.openEvents, go: () => goToApp("/events") }
                    : main === "organize"
                      ? { label: WIZARD_COPY.createEvent, go: () => goToApp("/events/new") }
                      : main === "community"
                        ? { label: WIZARD_COPY.createCircle, go: () => goToFinal("/circles?create=true") }
                        : { label: WIZARD_COPY.fillExpertCard, go: () => goToFinal("/profile?section=services") };
                return (
                  <button
                    type="button"
                    onClick={() => void cta.go()}
                    className="cta-solid h-14 w-full rounded-full text-lg font-semibold"
                  >
                    {cta.label}
                  </button>
                );
              })()}
            </div>
          </footer>
          <Dialog open={installOpen} onOpenChange={setInstallOpen}>
            <DialogContent className="max-w-[calc(100vw-2rem)] rounded-2xl sm:max-w-sm">
              <DialogTitle>{WIZARD_COPY.installTitle}</DialogTitle>
              <InstallGuide platform="ios" onInstalled={() => setInstallOpen(false)} />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <>
          <header className="z-10 shrink-0 border-b border-td-line bg-white px-5 pb-3 pt-[calc(env(safe-area-inset-top)+12px)]">
            <div className="mx-auto max-w-md">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-td-muted">
                  {steps ? `Шаг ${index + 1} из ${total}` : "\u00a0"}
                </span>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={signOut}
                    className="text-xs text-td-muted underline-offset-2 hover:underline"
                  >
                    Выйти из аккаунта
                  </button>
                  {!strictMode && (
                    <button
                      type="button"
                      onClick={closeWizard}
                      aria-label="Закрыть"
                      className="-mr-2 flex size-11 items-center justify-center rounded-full text-td-muted hover:bg-td-soft hover:text-td-dark"
                    >
                      <X className="size-5" />
                    </button>
                  )}
                </div>
              </div>
              <Progress value={total ? ((index + 1) / total) * 100 : 0} className="mt-2 h-2" />
            </div>
          </header>

          <main className="mx-auto min-h-0 w-full max-w-md flex-1 overflow-y-auto overscroll-contain px-5 py-6 [scroll-padding-bottom:35vh] [-webkit-overflow-scrolling:touch]">
            {current && index === 0 && (
              <div className="mb-5 flex items-center gap-3 rounded-2xl bg-td-soft/60 p-3">
                <MrDriveAvatar variant="people" size={48} />
                <p className="min-w-0 text-sm text-td-dark">
                  {meetMode
                    ? WIZARD_COPY.meetIntro
                    : arena
                    ? ARENA_COPY.wizardIntro
                    : `Мяу-merhaba! ${stepsLeftLabel(total)}, и ты в приложении 🐾`}
                </p>
              </div>
            )}
            {isLoading || !current ? (
              <p className="text-center text-sm text-td-muted">Загружаем…</p>
            ) : current.id === "intents" ? (
              <div className="space-y-4">
                <div>
                  <h1 className="font-display text-2xl font-bold text-td-dark">
                    {WIZARD_COPY.intentsTitle}
                  </h1>
                  <p className="mt-1 text-sm text-td-muted">{WIZARD_COPY.intentsHint}</p>
                </div>
                <div className="grid grid-cols-1 gap-2 min-[380px]:grid-cols-2">
                  {INTENT_CARDS.map((c) => {
                    const on = intents.includes(c.value);
                    return (
                      <button
                        key={c.value}
                        type="button"
                        aria-pressed={on}
                        onClick={() => setIntents(toggle(intents, c.value) as PlatformIntent[])}
                        className={`relative min-w-0 rounded-2xl border p-4 text-left transition ${on ? "border-td-teal bg-td-teal/10" : "border-td-line bg-white"}`}
                      >
                        {on && <Check className="absolute right-3 top-3 size-4 text-td-teal" />}
                        <span className="block pr-5 text-base font-semibold text-td-dark">{c.title}</span>
                        <span className="mt-1 block text-xs text-td-muted">{c.text}</span>
                      </button>
                    );
                  })}
                </div>
                <div>
                  <p className="text-sm font-medium text-td-dark">{WIZARD_COPY.extraLabel}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {INTENT_CHIPS.map((c) => {
                      const on = intents.includes(c.value);
                      return (
                        <button
                          key={c.value}
                          type="button"
                          aria-pressed={on}
                          onClick={() => setIntents(toggle(intents, c.value) as PlatformIntent[])}
                          className={`rounded-full border px-3 py-2 text-sm ${on ? "border-td-teal bg-td-teal/10 font-medium text-td-dark" : "border-td-line bg-white text-td-dark"}`}
                        >
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : current.id === "city" ? (
              <div className="space-y-4">
                <h1 className="font-display text-2xl font-bold text-td-dark">
                  {WIZARD_COPY.cityTitle}
                </h1>
                <p className="text-sm text-td-muted">{WIZARD_COPY.cityHint}</p>
                <CitySelect
                  id="setup-city"
                  value={city}
                  onChange={(v) => {
                    setCity(v);
                    // Выбрал реальный город — снимаем отметку «турист».
                    if (hasCity(v)) setTourist(false);
                  }}
                  className="[&_button]:h-12 [&_button]:text-base [&_input]:h-12 [&_input]:text-base"
                />
                <button
                  type="button"
                  onClick={() => setTourist((t) => !t)}
                  aria-pressed={tourist}
                  className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-medium text-td-dark ${tourist ? "border-td-teal bg-td-teal/10" : "border-td-line bg-white"}`}
                >
                  {WIZARD_COPY.touristLabel}
                  {tourist && <Check className="size-4 text-td-teal" />}
                </button>
              </div>
            ) : current.id === "interests" ? (
              <div className="space-y-6">
                {askName && (
                  <div>
                    <label htmlFor="setup-name" className="text-lg font-semibold text-td-dark">
                      Как тебя зовут?
                    </label>
                    <Input
                      id="setup-name"
                      className="mt-2 h-12 text-base"
                      placeholder="Имя и фамилия"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </div>
                )}
                <div>
                  <h1 className="font-display text-2xl font-bold text-td-dark">
                    {arena ? "Что тебе интересно?" : WIZARD_COPY.interestsTitle}
                  </h1>
                  <p className="mt-1 text-sm text-td-muted">
                    {arena
                      ? "По интересам я присылаю анонсы встреч и подбираю людей, с кем есть о чём поговорить"
                      : WIZARD_COPY.interestsHint}
                  </p>
                  <p
                    className={`mt-2 text-sm ${interests.length >= MIN_INTERESTS ? "text-td-teal" : "text-td-muted"}`}
                  >
                    Выбрано {Math.min(interests.length, MIN_INTERESTS)} из {MIN_INTERESTS}
                  </p>
                  <ChipPicker
                    options={INTEREST_OPTIONS}
                    selected={interests}
                    onToggle={(v) => setInterests(toggle(interests, v))}
                  />
                </div>
              </div>
            ) : current.id === "about" && arena ? (
              <div className="space-y-5">
                <div>
                  <h1 className="font-display text-2xl font-bold text-td-dark">Чем занимаешься?</h1>
                  <Input
                    className="mt-3 h-12 text-base"
                    placeholder="Например: дизайнер, риелтор, мама в декрете"
                    value={profession}
                    onChange={(e) => setProfession(e.target.value)}
                  />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-td-dark">{ARENA_COPY.aboutTitle}</h2>
                  <p className="mt-1 text-sm text-td-muted">{ARENA_COPY.aboutHint}</p>
                  <Textarea
                    className="mt-3 min-h-28 text-base"
                    placeholder={ARENA_COPY.aboutPlaceholder}
                    value={about}
                    maxLength={600}
                    onChange={(e) => setAbout(e.target.value)}
                  />
                </div>
              </div>
            ) : current.id === "seeking" ? (
              <div className="space-y-2">
                <h1 className="font-display text-2xl font-bold text-td-dark">
                  {ARENA_COPY.seekingTitle}
                </h1>
                <p className="text-sm text-td-muted">{ARENA_COPY.seekingHint}</p>
                <Textarea
                  className="mt-2 min-h-[8.5rem] text-base"
                  rows={6}
                  maxLength={LOOKING_FOR_MAX}
                  placeholder={ARENA_COPY.seekingPlaceholder}
                  value={seeking}
                  onChange={(e) => setSeeking(e.target.value.slice(0, LOOKING_FOR_MAX))}
                />
                <div className="flex items-start justify-between gap-3 text-xs text-td-muted">
                  <span>{ARENA_COPY.seekingExample}</span>
                  <span className="shrink-0 tabular-nums">
                    {seeking.length}/{LOOKING_FOR_MAX}
                  </span>
                </div>
              </div>
            ) : current.id === "purpose" ? (
              <div className="space-y-2">
                <h1 className="font-display text-2xl font-bold text-td-dark">
                  {ARENA_COPY.purposeTitle}
                </h1>
                <p className="text-sm text-td-muted">{ARENA_COPY.purposeHint}</p>
                <PurposePicker mode="tags" value={purpose} onChange={setPurpose} />
              </div>
            ) : current.id === "about" ? (
              <div className="space-y-6">
                <div>
                  <h1 className="font-display text-2xl font-bold text-td-dark">Чем занимаешься?</h1>
                  <p className="mt-1 text-sm text-td-muted">
                    Так тебя найдут люди из похожей сферы, а я подберу тех, с кем есть о чём
                    поговорить
                  </p>
                  <Input
                    className="mt-3 h-12 text-base"
                    placeholder="Например: дизайнер, риелтор, мама в декрете"
                    value={profession}
                    onChange={(e) => setProfession(e.target.value)}
                  />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-td-dark">Что ищешь в TurkeyDrive?</h2>
                  <p className="mt-1 text-sm text-td-muted">
                    Необязательно, можно выбрать несколько
                  </p>
                  <PurposePicker mode="tags" value={purpose} onChange={setPurpose} />
                </div>
              </div>
            ) : current.id === "meet" ? (
              <div className="space-y-4">
                <h1 className="font-display text-2xl font-bold text-td-dark">
                  Хочешь знакомиться с людьми?
                </h1>
                <p className="text-sm text-td-muted">
                  Я подберу тех, кто рядом по интересам, и покажу их в конце. Выбор можно поменять в
                  профиле в любой момент
                </p>
                <div className="flex flex-col gap-2">
                  {MEET_OPTIONS.map((o) => {
                    const active = meetChoice === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setMeetChoice(o.value)}
                        className={`rounded-xl border px-4 py-3 text-left ${active ? "border-td-teal bg-td-teal/10" : "border-td-line bg-white"}`}
                      >
                        <span className="flex items-center justify-between gap-2 text-base font-medium text-td-dark">
                          {o.label}
                          {active && <Check className="size-4 shrink-0 text-td-teal" />}
                        </span>
                        {o.note && (
                          <span className="mt-1 block text-xs text-td-muted">{o.note}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : current.id === "district" ? (
              <div className="space-y-4">
                <h1 className="font-display text-2xl font-bold text-td-dark">
                  В каком районе ты живёшь?
                </h1>
                <p className="text-sm text-td-muted">
                  Так я найду тебе соседей и встречи рядом с домом
                  {city ? ` · ${city}` : ""}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {districtList.map((d) => (
                    <button
                      key={d.name}
                      type="button"
                      disabled={saving}
                      onClick={() => chooseDistrict(d.name)}
                      className={`min-h-12 min-w-0 break-words rounded-xl border px-3 py-3 text-sm font-medium ${
                        district === d.name
                          ? "border-td-teal bg-td-teal text-white"
                          : "border-td-line bg-white text-td-dark"
                      }`}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    logFunnelEvent("wizard_step_skipped", "district");
                    void chooseDistrict(null);
                  }}
                  className="min-h-11 w-full rounded-full border border-td-line px-4 py-3 text-sm font-medium text-td-muted"
                >
                  Пока не знаю
                </button>
              </div>
            ) : current.id === "contact" ? (
              <div className="space-y-4">
                <h1 className="font-display text-2xl font-bold text-td-dark">
                  Как с тобой связаться?
                </h1>
                <p className="text-sm text-td-muted">
                  Чтобы тебе могли написать после приглашения на кофе. Твой контакт увидят участники
                  сообщества в твоём профиле. Скрыть его можно в любой момент: в профиле выбери
                  «Пока только изучаю»
                </p>
                <div>
                  <label htmlFor="setup-tg" className="text-sm font-medium text-td-dark">
                    Telegram
                  </label>
                  <Input
                    id="setup-tg"
                    className="mt-1 h-12 text-base"
                    placeholder="Telegram: @username"
                    value={telegram}
                    onChange={(e) => setTelegram(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="setup-wa" className="text-sm font-medium text-td-dark">
                    WhatsApp
                  </label>
                  <Input
                    id="setup-wa"
                    type="tel"
                    inputMode="tel"
                    className="mt-1 h-12 text-base"
                    placeholder="+90 5XX XXX XX XX"
                    value={whatsapp}
                    onChange={(e) => {
                      setWhatsapp(e.target.value);
                      setWaError(false);
                    }}
                  />
                  <p className="mt-1 text-xs text-td-muted">
                    Номер с кодом страны, например +90 5XX XXX XX XX
                  </p>
                  {waDigits ? (
                    <p className="mt-1 text-xs font-medium text-td-teal">
                      Сохраним как {formatWhatsapp(waDigits)}
                    </p>
                  ) : (
                    waError && <p className="mt-1 text-xs text-destructive">{WHATSAPP_ERROR}</p>
                  )}
                </div>
                {arena && (
                  <div>
                    <label htmlFor="setup-ig" className="text-sm font-medium text-td-dark">
                      {ARENA_COPY.instagramLabel}
                    </label>
                    <Input
                      id="setup-ig"
                      className="mt-1 h-12 text-base"
                      placeholder="@username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      value={instagram}
                      onChange={(e) => setInstagram(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-td-muted">{ARENA_COPY.instagramHint}</p>
                  </div>
                )}
                <p className="text-sm text-td-muted">Достаточно одного из двух</p>
              </div>
            ) : current.id === "photo" ? (
              <div className="space-y-6">
                {arena ? (
                  <div>
                    <h1 className="font-display text-2xl font-bold text-td-dark">{ARENA_COPY.photoTitle}</h1>
                    <div className="mt-3 flex items-center gap-3 rounded-2xl bg-td-soft/60 p-3">
                      <MrDriveAvatar variant="people" size={40} borderWidth={3} />
                      <p className="min-w-0 text-sm text-td-dark">{ARENA_COPY.photoHint}</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h1 className="font-display text-2xl font-bold text-td-dark">Добавь фото</h1>
                    <p className="mt-1 text-sm text-td-muted">С фото тебе чаще отвечают и зовут на кофе</p>
                  </div>
                )}
                {uid && (
                  <ProfilePhotoField
                    large
                    userId={uid}
                    value={photoUrl}
                    fullName={fullName}
                    onChange={setPhotoUrl}
                    selfie={arena}
                  />
                )}
              </div>
            ) : current.id === "services" ? (
              expertForm ? (
                <div className="space-y-6">
                  <div>
                    <h1 className="font-display text-2xl font-bold text-td-dark">Мои услуги</h1>
                    <p className="mt-2 text-sm text-td-muted">
                      Заполни обязательные поля — после сохранения тебя смогут найти во вкладке
                      «Эксперты».
                    </p>
                  </div>
                  <ExpertServiceFields value={expertDraft} onChange={setExpertDraft} />
                  <div className="space-y-2 pb-4">
                    <Button
                      type="button"
                      className="cta-solid h-12 w-full"
                      disabled={saving}
                      onClick={saveExpert}
                    >
                      {saving ? "Сохраняю…" : "Сохранить и продолжить"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      disabled={saving}
                      onClick={() => advance()}
                    >
                      Пропустить
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-full flex-col justify-center py-8 text-center">
                  <h1 className="font-display text-2xl font-bold text-td-dark">
                    💼 Оказываешь услуги или у тебя свой бизнес?
                  </h1>
                  <p className="mt-3 text-sm text-td-muted">
                    Заполни «Мои услуги» — получишь роль «Эксперт», и резиденты найдут тебя во
                    вкладке «Эксперты» в «Людях».
                  </p>
                  <div className="mt-6 space-y-2">
                    <Button
                      type="button"
                      className="cta-solid h-12 w-full"
                      onClick={() => setExpertForm(true)}
                    >
                      Да
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-12 w-full"
                      disabled={saving}
                      onClick={() => advance()}
                    >
                      Нет
                    </Button>
                  </div>
                  {uid && profile && (
                    <div className="mt-6">
                      <BloggerQuickBlock
                        userId={uid}
                        initialIsBlogger={!!profile.is_blogger}
                        initialUrl={profile.blog_url ?? ""}
                      />
                    </div>
                  )}
                </div>
              )
            ) : null}
          </main>

          {current && !(current.id === "services" && expertForm) && (
            <footer className="shrink-0 border-t border-td-line bg-white px-5 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3">
              <div className="mx-auto flex max-w-md items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIndex(Math.max(0, index - 1))}
                  disabled={index === 0 || saving}
                  className="rounded-full px-3 py-3 text-sm font-medium text-td-muted disabled:opacity-40"
                >
                  Назад
                </button>
                {current.kind === "optional" && current.id === "photo" && !(arena && photoUrl) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (arena && !photoUrl && !photoNudgeShown()) {
                        markPhotoNudgeShown();
                        setPhotoNudgeOpen(true);
                        return;
                      }
                      logFunnelEvent("wizard_step_skipped", current.id);
                      void advance();
                    }}
                    disabled={saving}
                    className="px-2 py-3 text-sm text-td-muted underline underline-offset-2"
                  >
                    {arena ? ARENA_COPY.photoLater : "Пропустить"}
                  </button>
                )}
                {current.id === "district" || current.id === "services" ? null : (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={nextDisabled}
                    className="cta-solid ml-auto min-h-11 rounded-full px-6 py-3 text-base font-semibold disabled:opacity-50"
                  >
                    {saving
                      ? "Сохраняю…"
                      : !arena && !meetMode && index === total - 1
                        ? WIZARD_COPY.done
                        : WIZARD_COPY.next}
                  </button>
                )}
              </div>
            </footer>
          )}
          {arena && (
            <>
              <input
                ref={selfieInputRef}
                type="file"
                accept="image/*"
                capture="user"
                className="sr-only"
                onChange={async (e) => {
                  const url = await avatarUpload.fromInput(e);
                  if (url) setPhotoUrl(url);
                }}
              />
              <AlertDialog open={photoNudgeOpen} onOpenChange={setPhotoNudgeOpen}>
                <AlertDialogContent className="max-w-[calc(100vw-2rem)] rounded-2xl sm:max-w-sm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{ARENA_COPY.photoSkipTitle}</AlertDialogTitle>
                    <AlertDialogDescription>{ARENA_COPY.photoSkipText}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      className="cta-solid flex min-h-12 items-center justify-center rounded-full px-5 text-base font-semibold"
                      onClick={() => {
                        setPhotoNudgeOpen(false);
                        selfieInputRef.current?.click();
                      }}
                    >
                      {ARENA_COPY.photoSelfie}
                    </button>
                    <button
                      type="button"
                      className="py-2 text-sm text-td-muted underline underline-offset-2"
                      onClick={() => {
                        setPhotoNudgeOpen(false);
                        logFunnelEvent("wizard_step_skipped", "photo");
                        void advance();
                      }}
                    >
                      {ARENA_COPY.photoSkipAnyway}
                    </button>
                  </div>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </>
      )}
    </div>
  );
}

const PHOTO_NUDGE_KEY = "arena-photo-nudge-shown";
function photoNudgeShown(): boolean {
  try {
    return sessionStorage.getItem(PHOTO_NUDGE_KEY) === "1";
  } catch {
    return false;
  }
}
function markPhotoNudgeShown() {
  try {
    sessionStorage.setItem(PHOTO_NUDGE_KEY, "1");
  } catch {
    /* ignore */
  }
}
